import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import VisitorLiveModal from '../../admin/VisitorLiveModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

// ─── Interfaces ──────────────────────────────────────────────────────────────

interface ActiveUser {
  id: string;
  email: string | null;
  visitorId?: string;
  ip: string;
  device: string;
  browser: string;
  currentPage: string;
  currentActivity: string;
  lastSeen: number;
  loginTime?: number;
  firstSeen?: number;
  isOnline: boolean;
  isAnonymous: boolean;
  isBot: boolean;
  type: 'user' | 'visitor';
  referrer?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
}

interface PaginationInfo {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface Stats {
  totalOnline: number;
  loggedInOnline: number;
  visitorsOnline: number;
  botsOnline: number;
  totalLoggedIn: number;
  totalVisitors: number;
  totalBots: number;
}

interface HeatmapCell {
  day: number;
  hour: number;
  count: number;
}

interface TopPage {
  page: string;
  count: number;
}

interface ReferrerEntry {
  referrer: string;
  count: number;
}

interface DailyEntry {
  _id: string;
  count: number;
  uniqueVisitors: number;
}

interface SubAdminSession {
  device: string;
  browser: string;
  ip: string;
  connectedAt: number;
}

interface SubAdminActivity {
  _id: string;
  email: string;
  name: string;
  lastLogin: string | null;
  loginCount: number;
  isActive: boolean;
  isCurrentlyOnline: boolean;
  activeSessions: number;
  sessionDetails: SubAdminSession[];
}

interface GateAttempt {
  _id: string;
  ip: string;
  userAgent: string;
  device: string;
  browser: string;
  attemptType: 'questions' | 'otp';
  submittedValues: any;
  success: boolean;
  attemptedAt: string;
}

// ─── Animations ──────────────────────────────────────────────────────────────

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

// UTM source brand colors
const SOURCE_COLORS: Record<string, string> = {
  facebook: '#1877F2', facebook_marketplace: '#0084FF', whatsapp: '#25D366',
  instagram: '#E4405F', twitter: '#1DA1F2', linkedin: '#0A66C2',
  youtube: '#FF0000', google: '#4285F4', email: '#EA4335',
  sms: '#6366f1', print: '#78716c', qr_code: '#0891b2',
};

// ─── Styled Components ───────────────────────────────────────────────────────

const PageContainer = styled.div`
  width: 100%;
  animation: ${fadeIn} 0.3s ease;
`;

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  background: #f1f5f9;
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 20px;
  width: fit-content;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 10px 24px;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${p => p.$active ? '#ffffff' : 'transparent'};
  color: ${p => p.$active ? '#1e293b' : '#64748b'};
  box-shadow: ${p => p.$active ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'};

  &:hover {
    color: #1e293b;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid #e8eaed;
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border-bottom: 1px solid #e8eaed;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  svg { width: 22px; height: 22px; color: white; }
`;

const HeaderTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const HeaderSubtitle = styled.p`
  font-size: 12px;
  color: #64748b;
  margin: 2px 0 0 0;
`;

const OnlineCount = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 20px;
  color: white;
  font-size: 13px;
  font-weight: 600;
`;

const OnlineDot = styled.div`
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 8px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e8eaed;
  flex-wrap: wrap;
`;

const StatBadge = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${p => p.$color}15;
  border-radius: 6px;
  font-size: 11px;
  color: ${p => p.$color};
  font-weight: 500;
`;

const UserList = styled.div`
  max-height: 600px;
  overflow-y: auto;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: #f1f5f9; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
`;

const UserRow = styled.div<{ $isOnline: boolean }>`
  display: grid;
  grid-template-columns: 54px 1fr auto auto;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid #f1f5f9;
  opacity: ${p => p.$isOnline ? 1 : 0.6};
  transition: background 0.15s ease;
  gap: 12px;
  &:last-child { border-bottom: none; }
  &:hover { background: #f8fafc; }
`;

const UserAvatar = styled.div<{ $isOnline: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${p => p.$isOnline
    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
  svg { width: 20px; height: 20px; color: white; }
`;

const StatusDot = styled.div<{ $isOnline: boolean }>`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${p => p.$isOnline ? '#10b981' : '#94a3b8'};
  border: 2px solid white;
`;

const UserInfo = styled.div`
  min-width: 0;
`;

const UserEmail = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const UserActivity = styled.div`
  font-size: 12px;
  color: #10b981;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
  svg { width: 12px; height: 12px; }
`;

const ReferrerTag = styled.div`
  font-size: 10px;
  color: #7c3aed;
  margin-top: 3px;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 320px;
  svg { width: 10px; height: 10px; flex-shrink: 0; }
`;

const MetaColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const DeviceBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #f1f5f9;
  border-radius: 6px;
  font-size: 11px;
  color: #475569;
  svg { width: 12px; height: 12px; }
`;

const IpBadge = styled.div`
  font-size: 10px;
  color: #94a3b8;
  font-family: 'SF Mono', 'Monaco', monospace;
`;

const RemoveBtn = styled.button`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: none;
  color: white;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 4px;
  &:hover:not(:disabled) { transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Badge = styled.span<{ $bg: string }>`
  background: ${p => p.$bg};
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
`;

const liveDotPulse = keyframes`
  0%, 100% { opacity: 1; box-shadow: 0 0 4px #22c55e; }
  50% { opacity: 0.5; box-shadow: 0 0 10px #22c55e, 0 0 20px rgba(34,197,94,0.3); }
`;

const ViewLiveBubble = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.68rem;
  font-weight: 600;
  color: #16a34a;
  cursor: pointer;
  padding: 3px 10px;
  border-radius: 10px;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: rgba(34, 197, 94, 0.2);
    border-color: rgba(34, 197, 94, 0.6);
  }
`;

const LiveDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 6px #22c55e;
  animation: ${liveDotPulse} 1.5s ease-in-out infinite;
  flex-shrink: 0;
`;

const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px 20px;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
  flex-wrap: wrap;
`;

const PageBtn = styled.button<{ $active?: boolean }>`
  background: ${p => p.$active ? 'linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%)' : '#ffffff'};
  color: ${p => p.$active ? 'white' : '#64748b'};
  border: 1px solid ${p => p.$active ? 'transparent' : '#e2e8f0'};
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover:not(:disabled) { background: ${p => p.$active ? '' : '#f1f5f9'}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const PageInfo = styled.span`
  font-size: 12px;
  color: #64748b;
  padding: 0 8px;
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #94a3b8;
  svg { width: 48px; height: 48px; margin-bottom: 12px; opacity: 0.5; }
  p { font-size: 14px; margin: 0; }
`;

const LoadingState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #64748b;
  font-size: 14px;
`;

// ─── Heatmap styled components ───────────────────────────────────────────────

const HeatmapContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const HeatmapGrid = styled.div`
  overflow-x: auto;
  padding: 0 20px 12px;
`;

const HeatmapTable = styled.table`
  border-collapse: separate;
  border-spacing: 3px;
  width: 100%;
  min-width: 700px;

  th {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    padding: 4px 2px;
    text-align: center;
  }

  td {
    text-align: center;
    font-size: 10px;
    font-weight: 600;
    border-radius: 4px;
    padding: 8px 2px;
    transition: transform 0.15s;
    cursor: default;
    position: relative;

    &:hover {
      transform: scale(1.15);
      z-index: 2;
    }
  }
`;

const DayLabel = styled.th`
  text-align: right !important;
  padding-right: 8px !important;
  white-space: nowrap;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  padding: 0 20px;
`;

const SummaryCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e8eaed;
  border-radius: 12px;
  padding: 16px;
`;

const SummaryLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const SummaryValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
`;

const BarContainer = styled.div`
  padding: 0 20px 20px;
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
`;

const BarLabel = styled.div`
  font-size: 12px;
  color: #475569;
  width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: right;
  flex-shrink: 0;
`;

const BarTrack = styled.div`
  flex: 1;
  height: 22px;
  background: #f1f5f9;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
`;

const BarFill = styled.div<{ $width: number; $color: string }>`
  height: 100%;
  width: ${p => p.$width}%;
  background: linear-gradient(135deg, ${p => p.$color} 0%, ${p => p.$color}cc 100%);
  border-radius: 6px;
  min-width: 2px;
  transition: width 0.4s ease;
`;

const BarCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  width: 45px;
  text-align: right;
  flex-shrink: 0;
`;

const SectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 12px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PeriodSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 12px;
  color: #475569;
  background: white;
  cursor: pointer;
  &:focus { outline: none; border-color: #4c6ef5; }
`;

const KickBtn = styled.button`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: none;
  color: white;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 2px 8px rgba(239,68,68,0.3); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SessionChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 10px;
  color: #475569;
  svg { width: 10px; height: 10px; }
`;

const AttemptRow = styled.div<{ $success: boolean }>`
  display: flex;
  align-items: flex-start;
  padding: 14px 20px;
  border-bottom: 1px solid #f1f5f9;
  gap: 14px;
  transition: background 0.15s ease;
  &:last-child { border-bottom: none; }
  &:hover { background: #f8fafc; }
  border-left: 3px solid ${p => p.$success ? '#10b981' : '#ef4444'};
`;

const AttemptValues = styled.pre`
  font-size: 11px;
  color: #64748b;
  background: #f8fafc;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  margin: 4px 0 0 0;
  white-space: pre-wrap;
  word-break: break-all;
  font-family: 'SF Mono', 'Monaco', monospace;
  max-height: 120px;
  overflow-y: auto;
`;

const AttemptMeta = styled.div`
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
`;

// ─── Helper Components ───────────────────────────────────────────────────────

const DeviceIcon: React.FC<{ device: string }> = ({ device }) => {
  if (device === 'Mobile') return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" /><line x1="12" y1="18" x2="12.01" y2="18" />
    </svg>
  );
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
};

// ─── Live Visitors Tab ───────────────────────────────────────────────────────

const LiveVisitorsTab: React.FC = () => {
  const [users, setUsers] = useState<ActiveUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loggingOut, setLoggingOut] = useState<string | null>(null);
  const [, setTick] = useState(0);
  const [liveViewVisitorId, setLiveViewVisitorId] = useState<string | null>(null);
  const [liveViewLabel, setLiveViewLabel] = useState<string>('');

  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const fetchUsers = useCallback(async (p: number = page) => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/active-users`, {
        params: { page: p, limit: 15 },
        withCredentials: true
      });
      if (res.data.success) {
        setUsers(res.data.activeUsers);
        setPagination(res.data.pagination);
        setStats(res.data.stats);
      }
    } catch (err) {
      console.error('Error fetching active users:', err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchUsers(page);
    const interval = setInterval(() => fetchUsers(page), 5000);
    return () => clearInterval(interval);
  }, [fetchUsers, page]);

  const handlePageChange = (p: number) => { setPage(p); fetchUsers(p); };

  const handleForceLogout = async (user: ActiveUser) => {
    if (!window.confirm(`${user.isAnonymous ? 'Remove' : 'Force logout'} ${user.email || user.visitorId}?`)) return;
    setLoggingOut(user.id);
    try {
      await axios.post(`${API_BASE}/api/admin/force-logout`, {
        email: user.isAnonymous ? undefined : user.email,
        visitorId: user.isAnonymous ? user.visitorId : undefined,
        redirectUrl: user.isAnonymous ? 'https://mngl.in' : undefined
      }, { withCredentials: true });
      fetchUsers(page);
    } catch { alert('Failed. Try again.'); }
    finally { setLoggingOut(null); }
  };

  const formatTimeAgo = (ts: number) => {
    const s = Math.floor((Date.now() - ts) / 1000);
    if (s < 60) return 'Just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    return `${Math.floor(m / 60)}h ago`;
  };

  const formatInactive = (ts: number) => {
    const s = Math.floor((Date.now() - ts) / 1000);
    if (s < 30) return null;
    const m = Math.floor(s / 60);
    return `${m}:${(s % 60).toString().padStart(2, '0')}`;
  };

  const formatReferrer = (ref: string | null | undefined) => {
    if (!ref) return null;
    try {
      const url = new URL(ref);
      return url.hostname + (url.pathname !== '/' ? url.pathname : '');
    } catch {
      return ref.substring(0, 50);
    }
  };

  const onlineCount = stats?.totalOnline || users.filter(u => u.isOnline).length;

  return (
    <Card>
      <CardHeader>
        <HeaderLeft>
          <HeaderIcon>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </HeaderIcon>
          <div>
            <HeaderTitle>Live Visitors</HeaderTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '2px' }}>
              <HeaderSubtitle style={{ margin: 0 }}>Real-time user activity with referrer tracking</HeaderSubtitle>
              {pagination && pagination.totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <PageBtn onClick={() => handlePageChange(1)} disabled={!pagination.hasPrev} style={{ padding: '2px 6px', fontSize: '10px', minWidth: 'auto', borderRadius: '4px' }}>First</PageBtn>
                  <PageBtn onClick={() => handlePageChange(page - 1)} disabled={!pagination.hasPrev} style={{ padding: '2px 5px', fontSize: '10px', minWidth: 'auto', borderRadius: '4px' }}>&laquo;</PageBtn>
                  {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <PageBtn key={pageNum} $active={pageNum === page} onClick={() => handlePageChange(pageNum)} style={{ padding: '2px 7px', fontSize: '10px', minWidth: '22px', borderRadius: '4px' }}>
                        {pageNum}
                      </PageBtn>
                    );
                  })}
                  <PageBtn onClick={() => handlePageChange(page + 1)} disabled={!pagination.hasNext} style={{ padding: '2px 5px', fontSize: '10px', minWidth: 'auto', borderRadius: '4px' }}>&raquo;</PageBtn>
                  <PageBtn onClick={() => handlePageChange(pagination.totalPages)} disabled={!pagination.hasNext} style={{ padding: '2px 6px', fontSize: '10px', minWidth: 'auto', borderRadius: '4px' }}>Last</PageBtn>
                  <PageInfo style={{ fontSize: '10px', padding: '0 4px' }}>{pagination.totalCount} total</PageInfo>
                </div>
              )}
            </div>
          </div>
        </HeaderLeft>
        <OnlineCount><OnlineDot />{onlineCount} Online</OnlineCount>
      </CardHeader>

      {stats && (
        <StatsRow>
          <StatBadge $color="#10b981">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
            {stats.loggedInOnline} Logged In
          </StatBadge>
          <StatBadge $color="#f59e0b">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>
            {stats.visitorsOnline} Visitors
          </StatBadge>
          <StatBadge $color="#8b5cf6">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="10" rx="2" /><circle cx="12" cy="5" r="2" /><path d="M12 7v4" /></svg>
            {stats.totalBots} Bots
          </StatBadge>
          <StatBadge $color="#64748b">Total: {stats.totalLoggedIn + stats.totalVisitors + stats.totalBots}</StatBadge>
        </StatsRow>
      )}

      {loading ? (
        <LoadingState>Loading visitors...</LoadingState>
      ) : users.length === 0 ? (
        <EmptyState>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="23" y1="11" x2="17" y2="11" />
          </svg>
          <p>No active visitors at the moment</p>
        </EmptyState>
      ) : (
        <>
          <UserList>
            {users.filter(user => {
              // Hide anonymous visitors / bots inactive for more than 15 seconds
              if ((user.isAnonymous || user.isBot) && (Date.now() - user.lastSeen) > 15000) return false;
              return true;
            }).map(user => (
              <UserRow key={user.id} $isOnline={user.isOnline}>
                <UserAvatar $isOnline={user.isOnline}>
                  {user.isBot ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="10" rx="2" /><circle cx="12" cy="5" r="2" /><path d="M12 7v4" /><circle cx="8" cy="16" r="1" /><circle cx="16" cy="16" r="1" /></svg>
                  ) : user.isAnonymous ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                  )}
                  <StatusDot $isOnline={user.isOnline} />
                </UserAvatar>

                <UserInfo>
                  <UserEmail>
                    {user.email || (user.isBot ? 'Bot/Crawler' : 'Anonymous Visitor')}
                    {user.isBot && <Badge $bg="linear-gradient(135deg, #8b5cf6, #7c3aed)">BOT</Badge>}
                    {user.isAnonymous && !user.isBot && <Badge $bg="linear-gradient(135deg, #f59e0b, #d97706)">VISITOR</Badge>}
                    {user.isOnline && (
                      <ViewLiveBubble
                        onClick={(e) => {
                          e.stopPropagation();
                          const visitorKey = user.visitorId || user.email || user.id;
                          setLiveViewVisitorId(visitorKey);
                          setLiveViewLabel(user.email || (user.isBot ? 'Bot/Crawler' : 'Anonymous Visitor'));
                        }}
                      >
                        <LiveDot />
                        View Live
                      </ViewLiveBubble>
                    )}
                    {formatInactive(user.lastSeen) && <Badge $bg="linear-gradient(135deg, #94a3b8, #64748b)">Inactive {formatInactive(user.lastSeen)}</Badge>}
                  </UserEmail>
                  <UserActivity>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                    {user.currentPage} &bull; {user.currentActivity}
                    <span style={{ color: '#94a3b8', marginLeft: '4px' }}>({formatTimeAgo(user.lastSeen)})</span>
                  </UserActivity>
                  {user.utm_source && (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.68rem',
                      fontWeight: 600, padding: '2px 8px', borderRadius: '4px', marginTop: '3px',
                      background: (SOURCE_COLORS[user.utm_source] || '#6366f1') + '1a',
                      color: SOURCE_COLORS[user.utm_source] || '#6366f1',
                      border: `1px solid ${(SOURCE_COLORS[user.utm_source] || '#6366f1')}33`,
                    }}>
                      📊 {user.utm_source}{user.utm_campaign ? ` / ${user.utm_campaign}` : ''}
                    </span>
                  )}
                  {user.referrer && (
                    <ReferrerTag title={user.referrer}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                      Referred from: {formatReferrer(user.referrer)}
                    </ReferrerTag>
                  )}
                </UserInfo>

                <MetaColumn>
                  <DeviceBadge>
                    <DeviceIcon device={user.device} />
                    {user.device} &bull; {user.browser}
                  </DeviceBadge>
                  <IpBadge>{user.ip}</IpBadge>
                </MetaColumn>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                  {user.isOnline && (
                    <RemoveBtn onClick={() => handleForceLogout(user)} disabled={loggingOut === user.id}>
                      {loggingOut === user.id ? '...' : user.isAnonymous ? 'Remove' : 'Logout'}
                    </RemoveBtn>
                  )}
                </div>
              </UserRow>
            ))}
          </UserList>

        </>
      )}

      {liveViewVisitorId && (
        <VisitorLiveModal
          visitorId={liveViewVisitorId}
          visitorLabel={liveViewLabel}
          onClose={() => { setLiveViewVisitorId(null); setLiveViewLabel(''); }}
        />
      )}
    </Card>
  );
};

// ─── Heatmap Tab ─────────────────────────────────────────────────────────────

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOUR_LABELS = Array.from({ length: 24 }, (_, i) => {
  const h = i % 12 || 12;
  return `${h}${i < 12 ? 'a' : 'p'}`;
});

const getHeatColor = (count: number, max: number): string => {
  if (count === 0) return '#f1f5f9';
  const ratio = count / max;
  if (ratio < 0.25) return '#dbeafe';
  if (ratio < 0.5) return '#93c5fd';
  if (ratio < 0.75) return '#3b82f6';
  return '#1d4ed8';
};

const VisitorHeatmapTab: React.FC = () => {
  const [heatmap, setHeatmap] = useState<HeatmapCell[]>([]);
  const [topPages, setTopPages] = useState<TopPage[]>([]);
  const [referrers, setReferrers] = useState<ReferrerEntry[]>([]);
  const [daily, setDaily] = useState<DailyEntry[]>([]);
  const [totalVisits, setTotalVisits] = useState(0);
  const [period, setPeriod] = useState(7);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/visitor-heatmap`, {
        params: { days: period },
        withCredentials: true
      });
      if (res.data.success) {
        setHeatmap(res.data.heatmap);
        setTopPages(res.data.topPages);
        setReferrers(res.data.referrers);
        setDaily(res.data.daily);
        setTotalVisits(res.data.totalVisits);
      }
    } catch (err) {
      console.error('Error fetching heatmap:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const maxCount = Math.max(...heatmap.map(h => h.count), 1);
  const maxPageCount = Math.max(...topPages.map(p => p.count), 1);
  const maxRefCount = Math.max(...referrers.map(r => r.count), 1);

  // Build heatmap grid data: day (1-7 Sun-Sat) x hour (0-23)
  const gridData: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
  heatmap.forEach(cell => {
    const dayIdx = cell.day - 1; // MongoDB $dayOfWeek: 1=Sun, 7=Sat
    if (dayIdx >= 0 && dayIdx < 7 && cell.hour >= 0 && cell.hour < 24) {
      gridData[dayIdx][cell.hour] = cell.count;
    }
  });

  const totalUniqueVisitors = daily.reduce((sum, d) => sum + d.uniqueVisitors, 0);

  if (loading) return <LoadingState>Loading heatmap data...</LoadingState>;

  return (
    <HeatmapContainer>
      {/* Period selector + summary cards */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px' }}>
        <SectionTitle style={{ padding: 0 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4c6ef5" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
          Visitor Heatmap
        </SectionTitle>
        <PeriodSelect value={period} onChange={e => setPeriod(Number(e.target.value))}>
          <option value={1}>Last 24 hours</option>
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </PeriodSelect>
      </div>

      <SummaryGrid>
        <SummaryCard>
          <SummaryLabel>Total Page Visits</SummaryLabel>
          <SummaryValue>{totalVisits.toLocaleString()}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Unique Visitors (by IP)</SummaryLabel>
          <SummaryValue>{totalUniqueVisitors.toLocaleString()}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Top Referrer</SummaryLabel>
          <SummaryValue style={{ fontSize: '16px', wordBreak: 'break-all' }}>
            {referrers.length > 0 ? (() => { try { return new URL(referrers[0].referrer).hostname; } catch { return referrers[0].referrer.substring(0, 30); } })() : 'None'}
          </SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Avg Daily Visits</SummaryLabel>
          <SummaryValue>{daily.length > 0 ? Math.round(totalVisits / daily.length).toLocaleString() : 0}</SummaryValue>
        </SummaryCard>
      </SummaryGrid>

      {/* Hour x Day Heatmap */}
      <Card style={{ margin: '0' }}>
        <div style={{ padding: '16px 20px 8px', borderBottom: '1px solid #e8eaed' }}>
          <SectionTitle style={{ padding: 0, margin: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4c6ef5" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
            Visits by Day &amp; Hour
          </SectionTitle>
        </div>
        <HeatmapGrid>
          <HeatmapTable>
            <thead>
              <tr>
                <th></th>
                {HOUR_LABELS.map((h, i) => <th key={i}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {DAY_NAMES.map((day, di) => (
                <tr key={day}>
                  <DayLabel>{day}</DayLabel>
                  {gridData[di].map((count, hi) => (
                    <td
                      key={hi}
                      style={{ background: getHeatColor(count, maxCount), color: count > maxCount * 0.5 ? '#fff' : '#475569' }}
                      title={`${day} ${HOUR_LABELS[hi]}: ${count} visits`}
                    >
                      {count > 0 ? count : ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </HeatmapTable>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end', marginTop: '8px', fontSize: '10px', color: '#94a3b8' }}>
            <span>Less</span>
            {[0, 0.25, 0.5, 0.75, 1].map((r, i) => (
              <div key={i} style={{ width: 16, height: 16, borderRadius: 3, background: getHeatColor(r * maxCount || (i === 0 ? 0 : 1), maxCount) }} />
            ))}
            <span>More</span>
          </div>
        </HeatmapGrid>
      </Card>

      {/* Top Pages */}
      <Card>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e8eaed' }}>
          <SectionTitle style={{ padding: 0, margin: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
            Top Pages
          </SectionTitle>
        </div>
        <BarContainer style={{ paddingTop: '16px' }}>
          {topPages.length === 0 ? (
            <p style={{ color: '#94a3b8', textAlign: 'center', fontSize: 13 }}>No page data in this period</p>
          ) : topPages.map((p, i) => (
            <BarRow key={i}>
              <BarLabel title={p.page}>{p.page || '/'}</BarLabel>
              <BarTrack>
                <BarFill $width={(p.count / maxPageCount) * 100} $color="#10b981" />
              </BarTrack>
              <BarCount>{p.count}</BarCount>
            </BarRow>
          ))}
        </BarContainer>
      </Card>

      {/* Referrers */}
      <Card>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e8eaed' }}>
          <SectionTitle style={{ padding: 0, margin: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
            Top Referrers
          </SectionTitle>
        </div>
        <BarContainer style={{ paddingTop: '16px' }}>
          {referrers.length === 0 ? (
            <p style={{ color: '#94a3b8', textAlign: 'center', fontSize: 13 }}>No referrer data in this period</p>
          ) : referrers.map((r, i) => {
            let label: string;
            try { label = new URL(r.referrer).hostname; } catch { label = r.referrer.substring(0, 40); }
            return (
              <BarRow key={i}>
                <BarLabel title={r.referrer}>{label}</BarLabel>
                <BarTrack>
                  <BarFill $width={(r.count / maxRefCount) * 100} $color="#7c3aed" />
                </BarTrack>
                <BarCount>{r.count}</BarCount>
              </BarRow>
            );
          })}
        </BarContainer>
      </Card>

      {/* Daily Trend */}
      <Card>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e8eaed' }}>
          <SectionTitle style={{ padding: 0, margin: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
            Daily Visit Trend
          </SectionTitle>
        </div>
        <BarContainer style={{ paddingTop: '16px' }}>
          {daily.length === 0 ? (
            <p style={{ color: '#94a3b8', textAlign: 'center', fontSize: 13 }}>No daily data</p>
          ) : (() => {
            const maxDaily = Math.max(...daily.map(d => d.count), 1);
            return daily.map((d, i) => (
              <BarRow key={i}>
                <BarLabel>{d._id}</BarLabel>
                <BarTrack>
                  <BarFill $width={(d.count / maxDaily) * 100} $color="#f59e0b" />
                </BarTrack>
                <BarCount>{d.count}</BarCount>
              </BarRow>
            ));
          })()}
        </BarContainer>
      </Card>
    </HeatmapContainer>
  );
};

// ─── Sub-admin Activity Tab ─────────────────────────────────────────────────

const SubAdminActivityTab: React.FC = () => {
  const [subAdmins, setSubAdmins] = useState<SubAdminActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [kicking, setKicking] = useState<string | null>(null);

  const fetchActivity = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/subadmin-activity`, {
        withCredentials: true
      });
      if (res.data.success) {
        setSubAdmins(res.data.subAdmins);
      }
    } catch (err) {
      console.error('Error fetching sub-admin activity:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivity();
    const interval = setInterval(fetchActivity, 10000);
    return () => clearInterval(interval);
  }, [fetchActivity]);

  const handleKick = async (sa: SubAdminActivity) => {
    if (!window.confirm(`Kick "${sa.name}" (${sa.email})?\n\nThey will be silently logged out from all browsers.`)) return;
    setKicking(sa._id);
    try {
      await axios.post(`${API_BASE}/api/admin/kick-subadmin/${sa._id}`, {}, { withCredentials: true });
      fetchActivity();
    } catch {
      alert('Failed to kick sub-admin. Try again.');
    } finally {
      setKicking(null);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const onlineCount = subAdmins.filter(sa => sa.isCurrentlyOnline).length;

  return (
    <Card>
      <CardHeader>
        <HeaderLeft>
          <HeaderIcon style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </HeaderIcon>
          <div>
            <HeaderTitle>Sub-admin Activity</HeaderTitle>
            <HeaderSubtitle>Monitor sub-admin login sessions and manage access</HeaderSubtitle>
          </div>
        </HeaderLeft>
        <OnlineCount style={{ background: onlineCount > 0
          ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
          : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)' }}>
          <OnlineDot />{onlineCount} Online
        </OnlineCount>
      </CardHeader>

      {loading ? (
        <LoadingState>Loading sub-admin activity...</LoadingState>
      ) : subAdmins.length === 0 ? (
        <EmptyState>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="48" height="48">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          <p>No sub-admin accounts configured</p>
        </EmptyState>
      ) : (
        <UserList>
          {subAdmins.map(sa => (
            <UserRow key={sa._id} $isOnline={sa.isCurrentlyOnline}>
              <UserAvatar $isOnline={sa.isCurrentlyOnline} style={{
                background: sa.isCurrentlyOnline
                  ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                  : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
              }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
                <StatusDot $isOnline={sa.isCurrentlyOnline} />
              </UserAvatar>

              <UserInfo>
                <UserEmail>
                  {sa.name}
                  {sa.isCurrentlyOnline && <Badge $bg="linear-gradient(135deg, #10b981, #059669)">ONLINE</Badge>}
                  {!sa.isActive && <Badge $bg="linear-gradient(135deg, #ef4444, #dc2626)">DISABLED</Badge>}
                  {sa.activeSessions > 1 && <Badge $bg="linear-gradient(135deg, #f59e0b, #d97706)">{sa.activeSessions} browsers</Badge>}
                </UserEmail>
                <UserActivity>
                  {sa.email}
                </UserActivity>
                <UserActivity>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  Last login: {formatDate(sa.lastLogin)} &bull; Total logins: {sa.loginCount}
                </UserActivity>
                {sa.sessionDetails.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                    {sa.sessionDetails.map((sess, i) => (
                      <SessionChip key={i}>
                        <DeviceIcon device={sess.device} />
                        {sess.device} &bull; {sess.browser} &bull; {sess.ip}
                      </SessionChip>
                    ))}
                  </div>
                )}
              </UserInfo>

              <MetaColumn>
                {sa.isCurrentlyOnline ? (
                  <KickBtn
                    onClick={() => handleKick(sa)}
                    disabled={kicking === sa._id}
                  >
                    {kicking === sa._id ? 'Kicking...' : 'Kick'}
                  </KickBtn>
                ) : (
                  <DeviceBadge style={{ color: '#94a3b8' }}>Offline</DeviceBadge>
                )}
              </MetaColumn>
            </UserRow>
          ))}
        </UserList>
      )}
    </Card>
  );
};

// ─── Audit Log Tab ──────────────────────────────────────────────────────────

const AuditLogTab: React.FC = () => {
  const [attempts, setAttempts] = useState<GateAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);

  const fetchAttempts = useCallback(async (p: number) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/admin/gate-attempts`, {
        params: { page: p, limit: 20 },
        withCredentials: true
      });
      if (res.data.success) {
        setAttempts(res.data.attempts);
        setPagination(res.data.pagination);
      }
    } catch (err) {
      console.error('Error fetching audit log:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAttempts(page);
  }, [fetchAttempts, page]);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatSubmittedValues = (attempt: GateAttempt): string => {
    if (!attempt.submittedValues) return 'N/A';
    if (attempt.attemptType === 'questions' && attempt.submittedValues.answers) {
      return attempt.submittedValues.answers
        .map((a: any) => `${a.label}: "${a.answer}"`)
        .join('\n');
    }
    if (attempt.attemptType === 'otp' && attempt.submittedValues.otp) {
      return `OTP entered: "${attempt.submittedValues.otp}"`;
    }
    return JSON.stringify(attempt.submittedValues, null, 2);
  };

  return (
    <Card>
      <CardHeader>
        <HeaderLeft>
          <HeaderIcon style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </HeaderIcon>
          <div>
            <HeaderTitle>Admin Gate Audit Log</HeaderTitle>
            <HeaderSubtitle>
              All attempts to access /admin/login gate
              {pagination && ` \u2014 ${pagination.totalCount} total entries`}
            </HeaderSubtitle>
          </div>
        </HeaderLeft>
      </CardHeader>

      {loading ? (
        <LoadingState>Loading audit log...</LoadingState>
      ) : attempts.length === 0 ? (
        <EmptyState>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="48" height="48">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          <p>No gate attempts recorded yet</p>
        </EmptyState>
      ) : (
        <>
          <UserList>
            {attempts.map(attempt => (
              <AttemptRow key={attempt._id} $success={attempt.success}>
                <UserAvatar $isOnline={false} style={{
                  background: attempt.success
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                    : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  width: '36px', height: '36px', minWidth: '36px'
                }}>
                  {attempt.success ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  )}
                </UserAvatar>

                <UserInfo style={{ flex: 1 }}>
                  <UserEmail>
                    {attempt.attemptType === 'questions' ? 'Security Questions' : 'OTP Verification'}
                    {attempt.success
                      ? <Badge $bg="linear-gradient(135deg, #10b981, #059669)">PASSED</Badge>
                      : <Badge $bg="linear-gradient(135deg, #ef4444, #dc2626)">FAILED</Badge>}
                  </UserEmail>
                  <UserActivity>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    {formatDate(attempt.attemptedAt)}
                  </UserActivity>
                  <AttemptValues>{formatSubmittedValues(attempt)}</AttemptValues>
                </UserInfo>

                <AttemptMeta>
                  <DeviceBadge>
                    <DeviceIcon device={attempt.device} />
                    {attempt.device} &bull; {attempt.browser}
                  </DeviceBadge>
                  <IpBadge>{attempt.ip}</IpBadge>
                </AttemptMeta>
              </AttemptRow>
            ))}
          </UserList>

          {pagination && pagination.totalPages > 1 && (
            <PaginationRow>
              <PageBtn onClick={() => setPage(1)} disabled={!pagination.hasPrev}>First</PageBtn>
              <PageBtn onClick={() => setPage(p => Math.max(1, p - 1))} disabled={!pagination.hasPrev}>&laquo;</PageBtn>
              {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                let pageNum: number;
                if (pagination.totalPages <= 5) { pageNum = i + 1; }
                else if (page <= 3) { pageNum = i + 1; }
                else if (page >= pagination.totalPages - 2) { pageNum = pagination.totalPages - 4 + i; }
                else { pageNum = page - 2 + i; }
                return (
                  <PageBtn key={pageNum} $active={pageNum === page} onClick={() => setPage(pageNum)}>
                    {pageNum}
                  </PageBtn>
                );
              })}
              <PageBtn onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))} disabled={!pagination.hasNext}>&raquo;</PageBtn>
              <PageBtn onClick={() => setPage(pagination.totalPages)} disabled={!pagination.hasNext}>Last</PageBtn>
              <PageInfo>{pagination.totalCount} entries</PageInfo>
            </PaginationRow>
          )}
        </>
      )}
    </Card>
  );
};

// ─── UTM Campaigns Tab ──────────────────────────────────────────────────────

interface UtmCampaign {
  _id: string;
  name: string;
  source: string;
  medium: string;
  campaign: string;
  content: string;
  term: string;
  fullUrl: string;
  shortCode: string;
  isActive: boolean;
  clickCount: number;
  visitors: number;
  uniqueVisitors: number;
  createdAt: string;
}

interface UtmAnalytics {
  bySource: { _id: string; visits: number; uniqueVisitors: number }[];
  byCampaign: { _id: { source: string; medium: string; campaign: string }; visits: number; uniqueVisitors: number }[];
  dailyBySource: { _id: { date: string; source: string }; visits: number }[];
  byMedium: { _id: string; visits: number; uniqueVisitors: number }[];
  totalUtm: number;
  totalOrganic: number;
  period: number;
}

// Preset channel templates like top ecom companies
const CHANNEL_PRESETS = [
  { label: 'Facebook', source: 'facebook', medium: 'social', icon: '📘' },
  { label: 'Facebook Marketplace', source: 'facebook_marketplace', medium: 'marketplace', icon: '🏪' },
  { label: 'WhatsApp', source: 'whatsapp', medium: 'social', icon: '💬' },
  { label: 'Instagram', source: 'instagram', medium: 'social', icon: '📸' },
  { label: 'Twitter / X', source: 'twitter', medium: 'social', icon: '🐦' },
  { label: 'LinkedIn', source: 'linkedin', medium: 'social', icon: '💼' },
  { label: 'YouTube', source: 'youtube', medium: 'video', icon: '📺' },
  { label: 'Google Ads', source: 'google', medium: 'cpc', icon: '🔍' },
  { label: 'Email', source: 'email', medium: 'email', icon: '📧' },
  { label: 'SMS', source: 'sms', medium: 'sms', icon: '📱' },
  { label: 'Print / Flyer', source: 'print', medium: 'offline', icon: '📄' },
  { label: 'QR Code (Offline)', source: 'qr_code', medium: 'offline', icon: '📲' },
];

// UTM arrival toast interface
interface UtmToast {
  id: string;
  source: string;
  medium: string;
  campaign: string;
  device: string;
  browser: string;
  ip: string;
  timestamp: number;
  isExiting: boolean;
}

const CampaignsTab: React.FC = () => {
  const [campaigns, setCampaigns] = useState<UtmCampaign[]>([]);
  const [analytics, setAnalytics] = useState<UtmAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [qrModalCampaign, setQrModalCampaign] = useState<UtmCampaign | null>(null);
  const qrRef = useRef<HTMLDivElement>(null);
  const [toasts, setToasts] = useState<UtmToast[]>([]);
  const [changedMetrics, setChangedMetrics] = useState<Set<string>>(new Set());
  const lastUtmTimestampRef = useRef<number>(Date.now());
  const prevCampaignsRef = useRef<Record<string, { visitors: number; uniqueVisitors: number; clickCount: number }>>({});

  // Form state
  const [formName, setFormName] = useState('');
  const [formSource, setFormSource] = useState('');
  const [formMedium, setFormMedium] = useState('');
  const [formCampaign, setFormCampaign] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formTerm, setFormTerm] = useState('');

  // Toast management
  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, isExiting: true } : t));
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 400);
  }, []);

  const addToast = useCallback((arrival: Omit<UtmToast, 'id' | 'isExiting'>) => {
    const id = `utm-${arrival.source}-${arrival.timestamp}-${Math.random().toString(36).slice(2, 6)}`;
    setToasts(prev => [{ ...arrival, id, isExiting: false }, ...prev].slice(0, 4));
    setTimeout(() => removeToast(id), 7000);
  }, [removeToast]);

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/utm-campaigns`, { withCredentials: true });
      if (res.data.success) {
        const newCampaigns: UtmCampaign[] = res.data.campaigns;
        // Detect metric changes for highlight animation
        const changed = new Set<string>();
        newCampaigns.forEach(c => {
          const prev = prevCampaignsRef.current[c._id];
          if (prev && (prev.visitors !== c.visitors || prev.uniqueVisitors !== c.uniqueVisitors || prev.clickCount !== c.clickCount)) {
            changed.add(c._id);
          }
        });
        if (changed.size > 0) {
          setChangedMetrics(changed);
          setTimeout(() => setChangedMetrics(new Set()), 2000);
        }
        // Update prev snapshot
        const snap: Record<string, { visitors: number; uniqueVisitors: number; clickCount: number }> = {};
        newCampaigns.forEach(c => { snap[c._id] = { visitors: c.visitors, uniqueVisitors: c.uniqueVisitors, clickCount: c.clickCount }; });
        prevCampaignsRef.current = snap;
        setCampaigns(newCampaigns);
      }
    } catch (err) { console.error('Failed to fetch campaigns:', err); }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/utm-analytics?days=${analyticsDays}`, { withCredentials: true });
      if (res.data.success) setAnalytics(res.data);
    } catch (err) { console.error('Failed to fetch UTM analytics:', err); }
  }, [analyticsDays]);

  // Poll for recent UTM arrivals (toast trigger)
  const fetchRecentArrivals = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/utm-recent?since=${lastUtmTimestampRef.current}`, { withCredentials: true });
      if (res.data.success && res.data.arrivals.length > 0) {
        res.data.arrivals.forEach((a: any) => {
          addToast({
            source: a.source, medium: a.medium, campaign: a.campaign,
            device: a.device, browser: a.browser, ip: a.ip, timestamp: a.timestamp,
          });
        });
        lastUtmTimestampRef.current = res.data.latestTimestamp;
        // Refresh metrics when new arrivals detected
        fetchCampaigns();
        fetchAnalytics();
      }
    } catch (err) { /* silent */ }
  }, [addToast, fetchCampaigns, fetchAnalytics]);

  // Initial load
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchCampaigns(), fetchAnalytics()]).finally(() => setLoading(false));
  }, [fetchCampaigns, fetchAnalytics]);

  // Auto-refresh: metrics every 10s, UTM arrivals every 5s
  useEffect(() => {
    const metricsInterval = setInterval(() => {
      fetchCampaigns();
      fetchAnalytics();
    }, 10000);
    const arrivalsInterval = setInterval(fetchRecentArrivals, 5000);
    return () => { clearInterval(metricsInterval); clearInterval(arrivalsInterval); };
  }, [fetchCampaigns, fetchAnalytics, fetchRecentArrivals]);

  const handlePreset = (preset: typeof CHANNEL_PRESETS[0]) => {
    setFormSource(preset.source);
    setFormMedium(preset.medium);
    setFormName(prev => prev || `${preset.label} Campaign`);
    setShowForm(true);
  };

  const handleCreate = async () => {
    if (!formName || !formSource || !formMedium || !formCampaign) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API_BASE}/api/admin/utm-campaigns`, {
        name: formName, source: formSource, medium: formMedium,
        campaign: formCampaign, content: formContent, term: formTerm,
      }, { withCredentials: true });
      if (res.data.success) {
        setCampaigns(prev => [{ ...res.data.campaign, visitors: 0, uniqueVisitors: 0 }, ...prev]);
        setFormName(''); setFormSource(''); setFormMedium('');
        setFormCampaign(''); setFormContent(''); setFormTerm('');
        setShowForm(false);
      }
    } catch (err) { console.error('Failed to create campaign:', err); }
    setCreating(false);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this campaign link?')) return;
    try {
      await axios.delete(`${API_BASE}/api/admin/utm-campaigns/${id}`, { withCredentials: true });
      setCampaigns(prev => prev.filter(c => c._id !== id));
    } catch (err) { console.error('Failed to delete campaign:', err); }
  };

  const handleToggle = async (id: string) => {
    try {
      const res = await axios.patch(`${API_BASE}/api/admin/utm-campaigns/${id}/toggle`, {}, { withCredentials: true });
      if (res.data.success !== undefined) {
        setCampaigns(prev => prev.map(c => c._id === id ? { ...c, isActive: res.data.isActive } : c));
      }
    } catch (err) { console.error('Failed to toggle campaign:', err); }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const downloadQr = (campaign: UtmCampaign) => {
    const svgEl = document.getElementById(`qr-${campaign._id}`)?.querySelector('svg');
    if (!svgEl) return;
    const svgData = new XMLSerializer().serializeToString(svgEl);
    const canvas = document.createElement('canvas');
    canvas.width = 1024; canvas.height = 1024;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      if (ctx) {
        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, 1024, 1024);
        ctx.drawImage(img, 0, 0, 1024, 1024);
      }
      const a = document.createElement('a');
      a.download = `${campaign.name.replace(/\s+/g, '_')}_QR.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const getSourceColor = (source: string) => SOURCE_COLORS[source] || '#6366f1';

  const formatTimeSince = (ts: number) => {
    const s = Math.floor((Date.now() - ts) / 1000);
    if (s < 5) return 'just now';
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    return `${Math.floor(s / 3600)}h ago`;
  };

  // Build preview URL
  const previewUrl = (() => {
    if (!formSource || !formCampaign) return '';
    const p = new URLSearchParams();
    p.set('utm_source', formSource); p.set('utm_medium', formMedium || 'social');
    p.set('utm_campaign', formCampaign);
    if (formContent) p.set('utm_content', formContent);
    if (formTerm) p.set('utm_term', formTerm);
    return `https://app.mngldodo.site?${p.toString()}`;
  })();

  return (
    <Card>
      <CardHeader style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
        <span style={{ fontSize: '1.2rem' }}>📊</span>
        <h3 style={{ color: '#fff', fontSize: '1rem', fontWeight: 700, margin: 0 }}>UTM Campaign Tracker</h3>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>Analytics period:</span>
          {[7, 14, 30, 90].map(d => (
            <button key={d} onClick={() => setAnalyticsDays(d)} style={{
              padding: '4px 10px', borderRadius: '6px', border: 'none', fontSize: '0.72rem', fontWeight: 600,
              cursor: 'pointer', background: analyticsDays === d ? '#fff' : 'rgba(255,255,255,0.15)',
              color: analyticsDays === d ? '#6366f1' : '#fff', transition: 'all 0.2s',
            }}>{d}d</button>
          ))}
        </div>
      </CardHeader>

      <div style={{ padding: '20px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Loading campaigns...</div>
        ) : (
          <>
            {/* ── Channel Overview Cards ─────────────────── */}
            {analytics && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                  <CampaignStatCard $bg="linear-gradient(135deg, #6366f1, #8b5cf6)">
                    <div style={{ fontSize: '1.5rem', fontWeight: 800 }}>{analytics.totalUtm}</div>
                    <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>Campaign Visits</div>
                  </CampaignStatCard>
                  <CampaignStatCard $bg="linear-gradient(135deg, #10b981, #059669)">
                    <div style={{ fontSize: '1.5rem', fontWeight: 800 }}>{analytics.totalOrganic}</div>
                    <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>Organic / Direct</div>
                  </CampaignStatCard>
                  <CampaignStatCard $bg="linear-gradient(135deg, #f59e0b, #d97706)">
                    <div style={{ fontSize: '1.5rem', fontWeight: 800 }}>{analytics.bySource.length}</div>
                    <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>Active Channels</div>
                  </CampaignStatCard>
                  <CampaignStatCard $bg="linear-gradient(135deg, #ec4899, #db2777)">
                    <div style={{ fontSize: '1.5rem', fontWeight: 800 }}>
                      {analytics.totalUtm + analytics.totalOrganic > 0
                        ? Math.round((analytics.totalUtm / (analytics.totalUtm + analytics.totalOrganic)) * 100)
                        : 0}%
                    </div>
                    <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>Campaign Attribution</div>
                  </CampaignStatCard>
                </div>

                {/* Channel Performance Bars */}
                {analytics.bySource.length > 0 && (
                  <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: '0.85rem', color: '#334155', fontWeight: 700 }}>📈 Channel Performance</h4>
                    {analytics.bySource.map((s, i) => {
                      const maxVisits = analytics.bySource[0]?.visits || 1;
                      const pct = Math.round((s.visits / maxVisits) * 100);
                      const color = getSourceColor(s._id);
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                          <div style={{ width: '130px', fontSize: '0.78rem', fontWeight: 600, color: '#475569', textTransform: 'capitalize' }}>
                            {s._id?.replace(/_/g, ' ')}
                          </div>
                          <div style={{ flex: 1, height: '22px', background: '#e2e8f0', borderRadius: '6px', overflow: 'hidden', position: 'relative' }}>
                            <div style={{
                              width: `${pct}%`, height: '100%', background: `linear-gradient(90deg, ${color}, ${color}dd)`,
                              borderRadius: '6px', transition: 'width 0.6s ease', minWidth: '2px',
                            }} />
                            <span style={{
                              position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                              fontSize: '0.68rem', fontWeight: 700, color: pct > 50 ? '#fff' : '#475569',
                            }}>{s.visits} visits · {s.uniqueVisitors} unique</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Medium Breakdown */}
                {analytics.byMedium.length > 0 && (
                  <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: '0.85rem', color: '#334155', fontWeight: 700 }}>🎯 By Medium</h4>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {analytics.byMedium.map((m, i) => (
                        <div key={i} style={{
                          padding: '8px 14px', borderRadius: '8px', background: '#fff', border: '1px solid #e2e8f0',
                          fontSize: '0.78rem', display: 'flex', gap: '8px', alignItems: 'center',
                        }}>
                          <span style={{ fontWeight: 700, color: '#334155', textTransform: 'capitalize' }}>{m._id}</span>
                          <span style={{ color: '#6366f1', fontWeight: 600 }}>{m.visits}</span>
                          <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>({m.uniqueVisitors} unique)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Quick Channel Presets ──────────────────── */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ margin: '0 0 10px', fontSize: '0.85rem', color: '#334155', fontWeight: 700 }}>⚡ Quick Create — Choose Channel</h4>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {CHANNEL_PRESETS.map(p => (
                  <ChannelPresetBtn key={p.source} onClick={() => handlePreset(p)} $color={getSourceColor(p.source)}>
                    <span>{p.icon}</span> {p.label}
                  </ChannelPresetBtn>
                ))}
                <ChannelPresetBtn onClick={() => { setShowForm(true); setFormSource(''); setFormMedium(''); }} $color="#64748b">
                  <span>➕</span> Custom
                </ChannelPresetBtn>
              </div>
            </div>

            {/* ── Create Campaign Form ──────────────────── */}
            {showForm && (
              <div style={{
                background: '#f8fafc', borderRadius: '12px', padding: '20px', marginBottom: '20px',
                border: '2px solid #6366f1', animation: 'fadeIn 0.3s ease',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h4 style={{ margin: 0, fontSize: '0.9rem', color: '#1e293b', fontWeight: 700 }}>🔗 Create Campaign Link</h4>
                  <button onClick={() => setShowForm(false)} style={{
                    background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#94a3b8', padding: '0 4px',
                  }}>×</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                  <CampaignInput>
                    <label>Campaign Name *</label>
                    <input value={formName} onChange={e => setFormName(e.target.value)} placeholder="e.g. March WhatsApp Push" />
                  </CampaignInput>
                  <CampaignInput>
                    <label>Source * <small>(utm_source)</small></label>
                    <input value={formSource} onChange={e => setFormSource(e.target.value)} placeholder="e.g. whatsapp, facebook" />
                  </CampaignInput>
                  <CampaignInput>
                    <label>Medium * <small>(utm_medium)</small></label>
                    <input value={formMedium} onChange={e => setFormMedium(e.target.value)} placeholder="e.g. social, cpc, email" />
                  </CampaignInput>
                  <CampaignInput>
                    <label>Campaign * <small>(utm_campaign)</small></label>
                    <input value={formCampaign} onChange={e => setFormCampaign(e.target.value)} placeholder="e.g. spring_2026, pune_launch" />
                  </CampaignInput>
                  <CampaignInput>
                    <label>Content <small>(utm_content)</small></label>
                    <input value={formContent} onChange={e => setFormContent(e.target.value)} placeholder="e.g. banner_v2, cta_top" />
                  </CampaignInput>
                  <CampaignInput>
                    <label>Term <small>(utm_term)</small></label>
                    <input value={formTerm} onChange={e => setFormTerm(e.target.value)} placeholder="e.g. cng dealership" />
                  </CampaignInput>
                </div>

                {/* URL Preview */}
                {previewUrl && (
                  <div style={{
                    background: '#1e293b', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px',
                    fontFamily: 'monospace', fontSize: '0.72rem', color: '#94a3b8', wordBreak: 'break-all',
                    lineHeight: 1.5,
                  }}>
                    <span style={{ color: '#22c55e' }}>https://</span>
                    <span style={{ color: '#e2e8f0' }}>app.mngldodo.site</span>
                    <span style={{ color: '#94a3b8' }}>?</span>
                    {previewUrl.split('?')[1]?.split('&').map((p, i) => {
                      const [k, v] = p.split('=');
                      return (
                        <span key={i}>
                          {i > 0 && <span style={{ color: '#94a3b8' }}>&</span>}
                          <span style={{ color: '#f59e0b' }}>{k}</span>
                          <span style={{ color: '#94a3b8' }}>=</span>
                          <span style={{ color: '#60a5fa' }}>{decodeURIComponent(v)}</span>
                        </span>
                      );
                    })}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '10px' }}>
                  <CreateCampaignBtn onClick={handleCreate} disabled={!formName || !formSource || !formMedium || !formCampaign || creating}>
                    {creating ? 'Creating...' : '✨ Generate Link & QR Code'}
                  </CreateCampaignBtn>
                  <button onClick={() => setShowForm(false)} style={{
                    padding: '10px 20px', borderRadius: '8px', border: '1px solid #e2e8f0',
                    background: '#fff', color: '#64748b', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem',
                  }}>Cancel</button>
                </div>
              </div>
            )}

            {/* ── Campaigns List ─────────────────────────── */}
            {campaigns.length > 0 ? (
              <div>
                <h4 style={{ margin: '0 0 12px', fontSize: '0.85rem', color: '#334155', fontWeight: 700 }}>
                  📋 Campaign Links ({campaigns.length})
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {campaigns.map(c => (
                    <CampaignCard key={c._id} $active={c.isActive}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>{c.name}</span>
                            <CampaignTag $bg={getSourceColor(c.source)}>{c.source}</CampaignTag>
                            <CampaignTag $bg="#64748b">{c.medium}</CampaignTag>
                            <CampaignTag $bg="#8b5cf6">{c.campaign}</CampaignTag>
                            {!c.isActive && <CampaignTag $bg="#ef4444">Paused</CampaignTag>}
                          </div>
                          <div style={{
                            fontFamily: 'monospace', fontSize: '0.7rem', color: '#64748b',
                            wordBreak: 'break-all', marginBottom: '8px', lineHeight: 1.5,
                          }}>
                            {c.fullUrl}
                          </div>
                          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                            <CampaignStat className={changedMetrics.has(c._id) ? 'metric-flash' : ''}>
                              <span style={{ color: '#6366f1' }}>🔗</span> <MetricValue $changed={changedMetrics.has(c._id)}>{c.clickCount}</MetricValue> clicks
                            </CampaignStat>
                            <CampaignStat className={changedMetrics.has(c._id) ? 'metric-flash' : ''}>
                              <span style={{ color: '#22c55e' }}>👁</span> <MetricValue $changed={changedMetrics.has(c._id)}>{c.visitors}</MetricValue> visits
                            </CampaignStat>
                            <CampaignStat className={changedMetrics.has(c._id) ? 'metric-flash' : ''}>
                              <span style={{ color: '#f59e0b' }}>👤</span> <MetricValue $changed={changedMetrics.has(c._id)}>{c.uniqueVisitors}</MetricValue> unique
                            </CampaignStat>
                            <CampaignStat>
                              <span style={{ color: '#94a3b8' }}>📅</span> {new Date(c.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </CampaignStat>
                          </div>
                        </div>

                        {/* QR + Actions */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                          <div id={`qr-${c._id}`} onClick={() => setQrModalCampaign(c)} style={{ cursor: 'pointer', padding: '4px', background: '#fff', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                            <QRCodeSVG value={c.fullUrl} size={64} level="M" />
                          </div>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <CampaignActionBtn title="Copy Link" onClick={() => copyToClipboard(c.fullUrl, c._id)}>
                              {copiedId === c._id ? '✅' : '📋'}
                            </CampaignActionBtn>
                            <CampaignActionBtn title="Download QR" onClick={() => downloadQr(c)}>⬇️</CampaignActionBtn>
                            <CampaignActionBtn title={c.isActive ? 'Pause' : 'Activate'} onClick={() => handleToggle(c._id)}>
                              {c.isActive ? '⏸️' : '▶️'}
                            </CampaignActionBtn>
                            <CampaignActionBtn title="Delete" onClick={() => handleDelete(c._id)} style={{ color: '#ef4444' }}>🗑️</CampaignActionBtn>
                          </div>
                        </div>
                      </div>
                    </CampaignCard>
                  ))}
                </div>
              </div>
            ) : (
              !showForm && (
                <div style={{
                  textAlign: 'center', padding: '40px 20px', color: '#94a3b8',
                  background: '#f8fafc', borderRadius: '12px', border: '2px dashed #e2e8f0',
                }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>🔗</div>
                  <div style={{ fontWeight: 600, color: '#475569', fontSize: '1rem', marginBottom: '4px' }}>No Campaign Links Yet</div>
                  <div style={{ fontSize: '0.82rem', marginBottom: '16px' }}>Create UTM-tracked links for Facebook, WhatsApp, and other channels to measure visitor acquisition.</div>
                  <CreateCampaignBtn onClick={() => setShowForm(true)}>+ Create Your First Campaign</CreateCampaignBtn>
                </div>
              )
            )}
          </>
        )}
      </div>

      {/* QR Code Enlargement Modal */}
      {qrModalCampaign && (
        <QrModal onClick={() => setQrModalCampaign(null)}>
          <QrModalContent onClick={e => e.stopPropagation()} ref={qrRef}>
            <h3 style={{ margin: '0 0 4px', fontSize: '1rem', color: '#1e293b' }}>{qrModalCampaign.name}</h3>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '16px' }}>
              Scan to visit via <strong>{qrModalCampaign.source}</strong> / {qrModalCampaign.medium}
            </div>
            <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'inline-block' }}>
              <QRCodeSVG value={qrModalCampaign.fullUrl} size={256} level="H" includeMargin />
            </div>
            <div style={{
              marginTop: '12px', fontFamily: 'monospace', fontSize: '0.7rem', color: '#64748b',
              wordBreak: 'break-all', maxWidth: '320px', textAlign: 'center',
            }}>
              {qrModalCampaign.fullUrl}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <CreateCampaignBtn onClick={() => { downloadQr(qrModalCampaign); }}>
                ⬇️ Download QR (PNG)
              </CreateCampaignBtn>
              <button onClick={() => copyToClipboard(qrModalCampaign.fullUrl, 'modal')} style={{
                padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0',
                background: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem',
              }}>
                {copiedId === 'modal' ? '✅ Copied!' : '📋 Copy Link'}
              </button>
            </div>
            <button onClick={() => setQrModalCampaign(null)} style={{
              position: 'absolute', top: '12px', right: '12px', background: 'none', border: 'none',
              fontSize: '1.3rem', cursor: 'pointer', color: '#94a3b8', padding: '0 4px',
            }}>×</button>
          </QrModalContent>
        </QrModal>
      )}

      {/* UTM Arrival Toast Notifications */}
      {toasts.length > 0 && (
        <UtmToastContainer>
          {toasts.map((t, idx) => {
            const srcColor = getSourceColor(t.source);
            const channelIcon = CHANNEL_PRESETS.find(p => p.source === t.source)?.icon || '📊';
            return (
              <UtmToastItem key={t.id} $isExiting={t.isExiting} $idx={idx} $color={srcColor}>
                <UtmToastShimmer $color={srcColor} />
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', position: 'relative', zIndex: 1 }}>
                  <UtmToastIcon $color={srcColor}>
                    <span style={{ fontSize: '1.1rem' }}>{channelIcon}</span>
                  </UtmToastIcon>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b', marginBottom: '1px' }}>
                      New visitor from <span style={{ color: srcColor, textTransform: 'capitalize' }}>{t.source.replace(/_/g, ' ')}</span>
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                      {t.campaign} · {t.device} · {t.browser}
                    </div>
                  </div>
                  <UtmToastTime>{formatTimeSince(t.timestamp)}</UtmToastTime>
                  <button onClick={() => removeToast(t.id)} style={{
                    background: 'none', border: 'none', fontSize: '1rem', cursor: 'pointer',
                    color: '#94a3b8', padding: '0 2px', lineHeight: 1,
                  }}>×</button>
                </div>
                <UtmToastProgress $color={srcColor} />
              </UtmToastItem>
            );
          })}
        </UtmToastContainer>
      )}
    </Card>
  );
};

// Campaign-specific styled components
const CampaignStatCard = styled.div<{ $bg: string }>`
  background: ${p => p.$bg};
  color: #fff;
  padding: 16px 20px;
  border-radius: 12px;
  min-width: 140px;
  flex: 1;
`;

const ChannelPresetBtn = styled.button<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  border: 2px solid ${p => p.$color}33;
  background: ${p => p.$color}0d;
  color: ${p => p.$color};
  font-weight: 600;
  font-size: 0.78rem;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background: ${p => p.$color}1a;
    border-color: ${p => p.$color}66;
    transform: translateY(-1px);
  }
`;

const CampaignInput = styled.div`
  label {
    display: block;
    font-size: 0.72rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 4px;
    small { color: #94a3b8; font-weight: 400; }
  }
  input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.82rem;
    outline: none;
    transition: border-color 0.2s;
    &:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }
  }
`;

const CreateCampaignBtn = styled.button`
  padding: 10px 24px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
  font-weight: 700;
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.2s;
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(99,102,241,0.3); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CampaignCard = styled.div<{ $active: boolean }>`
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid ${p => p.$active ? '#e2e8f0' : '#fecaca'};
  background: ${p => p.$active ? '#fff' : '#fef2f2'};
  transition: all 0.2s;
  &:hover { box-shadow: 0 2px 12px rgba(0,0,0,0.06); }
`;

const CampaignTag = styled.span<{ $bg: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.65rem;
  font-weight: 600;
  color: #fff;
  background: ${p => p.$bg};
`;

const CampaignStat = styled.span`
  font-size: 0.75rem;
  color: #475569;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const CampaignActionBtn = styled.button`
  padding: 4px 6px;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.75rem;
  transition: all 0.2s;
  &:hover { background: #f1f5f9; transform: scale(1.1); }
`;

const QrModal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: ${fadeIn} 0.2s ease;
`;

const QrModalContent = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 28px;
  text-align: center;
  position: relative;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  max-width: 420px;
  width: 90%;
`;

// Metric value with highlight animation
const metricFlash = keyframes`
  0% { transform: scale(1); }
  30% { transform: scale(1.35); color: #6366f1; }
  60% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const MetricValue = styled.span<{ $changed: boolean }>`
  font-weight: 700;
  display: inline-block;
  transition: color 0.3s;
  animation: ${p => p.$changed ? metricFlash : 'none'} 0.6s ease;
`;

// Toast animations
const toastSlideIn = keyframes`
  0% { transform: translateX(120%); opacity: 0; }
  100% { transform: translateX(0); opacity: 1; }
`;
const toastSlideOut = keyframes`
  0% { transform: translateX(0); opacity: 1; max-height: 100px; margin-bottom: 8px; }
  100% { transform: translateX(120%); opacity: 0; max-height: 0; margin-bottom: 0; }
`;
const toastProgressShrink = keyframes`
  from { width: 100%; }
  to { width: 0%; }
`;
const shimmerSlide = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(200%); }
`;

const UtmToastContainer = styled.div`
  position: fixed;
  top: 80px;
  right: 20px;
  z-index: 100000;
  display: flex;
  flex-direction: column;
  gap: 0;
  max-width: 380px;
  width: 90vw;
  pointer-events: none;
  & > * { pointer-events: auto; }
`;

const UtmToastItem = styled.div<{ $isExiting: boolean; $idx: number; $color: string }>`
  background: #ffffff;
  border: 1px solid ${p => p.$color}44;
  border-left: 4px solid ${p => p.$color};
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 8px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06);
  animation: ${p => p.$isExiting ? toastSlideOut : toastSlideIn} ${p => p.$isExiting ? '0.4s' : '0.5s'} cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: ${p => p.$isExiting ? '0s' : `${p.$idx * 0.05}s`};
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(8px);
`;

const UtmToastShimmer = styled.div<{ $color: string }>`
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, ${p => p.$color}0d, transparent);
  animation: ${shimmerSlide} 3s ease-in-out infinite;
  pointer-events: none;
`;

const UtmToastIcon = styled.div<{ $color: string }>`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.$color}15;
  border: 1px solid ${p => p.$color}33;
  flex-shrink: 0;
`;

const UtmToastTime = styled.span`
  font-size: 0.65rem;
  color: #94a3b8;
  white-space: nowrap;
  font-weight: 500;
`;

const UtmToastProgress = styled.div<{ $color: string }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: linear-gradient(90deg, ${p => p.$color}, ${p => p.$color}88);
  border-radius: 0 0 0 12px;
  animation: ${toastProgressShrink} 7s linear forwards;
`;

// ─── Main Page Component ─────────────────────────────────────────────────────

const VisitorStatsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'live' | 'heatmap' | 'campaigns' | 'subadmin' | 'audit'>('live');

  return (
    <PageContainer>
      <TabBar>
        <Tab $active={activeTab === 'live'} onClick={() => setActiveTab('live')}>
          Live Visitors
        </Tab>
        <Tab $active={activeTab === 'heatmap'} onClick={() => setActiveTab('heatmap')}>
          Visitor Heatmap
        </Tab>
        <Tab $active={activeTab === 'campaigns'} onClick={() => setActiveTab('campaigns')} style={activeTab === 'campaigns' ? { background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff' } : {}}>
          📊 Campaigns
        </Tab>
        <Tab $active={activeTab === 'subadmin'} onClick={() => setActiveTab('subadmin')}>
          Sub-admin Activity
        </Tab>
        <Tab $active={activeTab === 'audit'} onClick={() => setActiveTab('audit')}>
          Audit Log
        </Tab>
      </TabBar>

      {activeTab === 'live' && <LiveVisitorsTab />}
      {activeTab === 'heatmap' && <VisitorHeatmapTab />}
      {activeTab === 'campaigns' && <CampaignsTab />}
      {activeTab === 'subadmin' && <SubAdminActivityTab />}
      {activeTab === 'audit' && <AuditLogTab />}
    </PageContainer>
  );
};

export default VisitorStatsPage;
